import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`w-4 h-4 ${s <= rating ? "fill-secondary text-secondary" : "text-border fill-muted"}`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((res) => setReviews(res.data)).catch(() => {});
  }, []);

  if (!reviews.length) return null;

  return (
    <section id="reviews" className="py-24 md:py-32 relative overflow-hidden" data-testid="reviews-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-secondary/10 blur-3xl rounded-full" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <span className="font-monotech text-xs tracking-[0.25em] uppercase text-secondary-foreground/70">
            Trusted Voices
          </span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-primary mt-4">
            Clinicians & families<br />
            <span className="font-serifaccent italic font-normal">who trust the seal.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {reviews.map((r, i) => (
            <motion.figure
              key={r.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="border border-border bg-card rounded-3xl p-7 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
              data-testid={`review-card-${i}`}
            >
              <div className="flex items-center justify-between">
                <Stars rating={r.rating} />
                <Quote className="w-6 h-6 text-secondary/40" />
              </div>
              <blockquote className="mt-5 text-sm md:text-base text-text-muted leading-relaxed flex-1">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-border">
                <div className="font-heading font-bold text-primary">{r.name}</div>
                <div className="text-xs text-text-muted mt-0.5">{r.role}</div>
                <span className="inline-block mt-3 font-monotech text-[10px] tracking-widest uppercase bg-muted text-primary px-3 py-1 rounded-full">
                  {r.product}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
